import React, { useState } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { adminApi } from '../../api';
import { toast } from '../../hooks/use-toast';
import { Plus, Trash2, Edit2, X, Save } from 'lucide-react';

const emptyForm = () => ({
  kind: 'USDT', label: '', address: '', network: 'TRC-20', qr_url: '', active: true, notes: '',
});

const PaymentsAdmin = () => {
  const { payments, refreshAll } = useAdmin();
  const [form, setForm] = useState(null);

  const openNew = () => setForm({ id: null, ...emptyForm() });
  const openEdit = (p) => setForm({ ...p });
  const close = () => setForm(null);

  const save = async () => {
    try {
      const payload = {
        kind: form.kind, label: form.label, address: form.address,
        network: form.network || '', qr_url: form.qr_url || '',
        active: !!form.active, notes: form.notes || '',
      };
      if (form.id) await adminApi.updatePaymentMethod(form.id, payload);
      else await adminApi.createPaymentMethod(payload);
      toast({ title: 'Saved' });
      refreshAll();
      close();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again' }); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete payment method "${p.label}"?`)) return;
    try {
      await adminApi.deletePaymentMethod(p.id);
      toast({ title: 'Deleted' });
      refreshAll();
    } catch (e) { toast({ title: 'Failed' }); }
  };

  return (
    <div className="px-8 py-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.25em] text-[#5a5a5a] font-mono">PAYMENT METHODS</div>
          <div className="text-2xl text-[#e6e6e6] font-mono">{payments.length}</div>
        </div>
        <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.2em] rounded-md border border-[#22c55e] text-[#22c55e] hover:bg-[#0f1a12] font-mono">
          <Plus size={13} /> ADD METHOD
        </button>
      </div>

      {payments.length === 0 ? (
        <div className="border border-dashed border-[#1f1f1f] rounded-md py-16 text-center text-[#5a5a5a] font-mono text-[12px]">
          No payment methods yet. Add USDT wallet address or UPI ID to start receiving payments.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 font-mono">
          {payments.map((p) => (
            <div key={p.id} className="border border-[#151515] bg-[#0a0a0a] rounded-md p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] tracking-[0.25em] px-2 py-0.5 rounded bg-[#0f1a12] text-[#22c55e] border border-[#1f3a24]">{p.kind}</span>
                    {!p.active && <span className="text-[10px] tracking-[0.25em] px-2 py-0.5 rounded bg-[#1a1a1a] text-[#8a8a8a]">INACTIVE</span>}
                  </div>
                  <div className="text-[13px] text-[#e6e6e6] mt-2">{p.label}</div>
                  {p.network && <div className="text-[11px] text-[#8a8a8a]">{p.network}</div>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(p)} className="p-1.5 rounded border border-[#1f1f1f] text-[#8a8a8a] hover:border-[#22c55e] hover:text-[#22c55e]"><Edit2 size={12} /></button>
                  <button onClick={() => remove(p)} className="p-1.5 rounded border border-[#1f1f1f] text-[#8a8a8a] hover:border-[#ef4444] hover:text-[#ef4444]"><Trash2 size={12} /></button>
                </div>
              </div>
              <code className="block text-[11px] text-[#22c55e] break-all mt-3 bg-[#050505] border border-[#151515] rounded-md p-2">{p.address}</code>
              {p.notes && <p className="text-[11px] text-[#8a8a8a] mt-2">{p.notes}</p>}
            </div>
          ))}
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 overflow-y-auto">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={close} />
          <div className="relative w-full max-w-[520px] border border-[#1f1f1f] bg-[#0a0a0a] rounded-md p-6 font-mono my-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg text-[#e6e6e6]">{form.id ? 'Edit' : 'New'} Payment Method</h2>
              <button onClick={close} className="text-[#5a5a5a] hover:text-[#e6e6e6]"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-1">TYPE</label>
                <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="w-full bg-[#0a0a0a] border border-[#1f1f1f] rounded-md px-3 py-2 text-sm outline-none focus:border-[#22c55e]">
                  <option value="USDT">USDT (Crypto)</option>
                  <option value="UPI">UPI (INR)</option>
                  <option value="OTHER">OTHER</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-1">LABEL</label>
                <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Main USDT Wallet" className="w-full bg-transparent border border-[#1f1f1f] rounded-md px-3 py-2 text-sm outline-none focus:border-[#22c55e]" />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-1">ADDRESS / UPI ID</label>
                <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="TRC20 wallet address or user@upi" className="w-full bg-transparent border border-[#1f1f1f] rounded-md px-3 py-2 text-sm outline-none focus:border-[#22c55e]" />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-1">NETWORK (optional)</label>
                <input value={form.network} onChange={(e) => setForm({ ...form, network: e.target.value })} placeholder="TRC-20 / BEP-20 / ..." className="w-full bg-transparent border border-[#1f1f1f] rounded-md px-3 py-2 text-sm outline-none focus:border-[#22c55e]" />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-1">QR CODE URL (optional)</label>
                <input value={form.qr_url} onChange={(e) => setForm({ ...form, qr_url: e.target.value })} placeholder="https://..." className="w-full bg-transparent border border-[#1f1f1f] rounded-md px-3 py-2 text-sm outline-none focus:border-[#22c55e]" />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-1">NOTES (shown to customer)</label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full bg-transparent border border-[#1f1f1f] rounded-md px-3 py-2 text-sm outline-none focus:border-[#22c55e] resize-none" />
              </div>
              <label className="flex items-center gap-2 text-[12px] text-[#8a8a8a]">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active (visible to users)
              </label>
            </div>
            <button onClick={save} className="mt-5 w-full py-2.5 rounded-md text-[11px] tracking-[0.25em] bg-[#166534] hover:bg-[#15803d] text-[#e7ffef]"><Save size={12} className="inline mr-1" /> SAVE</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsAdmin;
